/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import FormEmployeeViewmodel from '../viewmodels/form-employee.viewmodel';
import { EmployeeEntity } from '@/domains/employee/entities';
import { Button, Typography, Modal, Space, Input, notification } from 'antd';
import CoreSelect from '@/components/Form/SelectAntForm';
import { useCoreContext } from '@/core';
import UpdateBaseIdEmployeeViewModel from '../viewmodels/update-base-id.viewmodel';
import { getErrorMessage } from '@/components/hooks/useGetErrorMessage';

interface Props {
  detail: EmployeeEntity;
}
export const FormEmployee: React.FC<Props> = ({ detail }) => {
  const { methodForm, onSubmit } = FormEmployeeViewmodel(detail);
  const { workShifts } = useCoreContext();
  const { actionUpdateBaseId, loading: loadingBaseId } = UpdateBaseIdEmployeeViewModel();

  const { control } = methodForm;
  const [showLinkUserModal, setShowLinkUserModal] = useState(false);
  const [baseIdInput, setBaseIdInput] = useState<string>('');

  useEffect(() => {
    methodForm.reset(detail);
  }, [detail?.id]);

  useEffect(() => {
    if (showLinkUserModal && detail?.baseId) {
      setBaseIdInput(detail.baseId);
    }
  }, [showLinkUserModal, detail?.baseId]);

  const confirmOverwrite = (content: string) =>
    new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: 'Update User Link',
        content,
        okText: 'Yes, overwrite',
        okButtonProps: { danger: true },
        cancelText: 'No, Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const submitLinkUser = async (baseId: string) => {
    try {
      await actionUpdateBaseId(detail.employeeCode, baseId);
      setShowLinkUserModal(false);
      setBaseIdInput('');
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error) || 'Failed to link employee to user',
        placement: 'bottomRight',
      });
    }
  };

  const handleLinkUser = async () => {
    if (!baseIdInput.trim() || !detail?.employeeCode) {
      return;
    }

    // The server never refuses a User ID that is already taken -- it unlinks the
    // previous holder -- so this confirm is the only warning the operator gets.
    if (detail.baseId) {
      const confirmed = await confirmOverwrite(
        'This employee is already linked to a user. Do you want to update the link?',
      );
      if (!confirmed) {
        return;
      }
    }

    await submitLinkUser(baseIdInput.trim());
  };

  return (
    <FormProvider {...methodForm}>
      <form onSubmit={onSubmit}>
        <Typography.Title level={4}>Thông tin nhân viên</Typography.Title>
        <div className='flex gap-2'>
          <Typography className='w-40 text-lg font-medium'>Mã nhân viên: </Typography>
          <Typography className='text-base'>{detail.employeeCode}</Typography>
        </div>
        <div className='flex gap-2'>
          <Typography className='w-40 text-lg font-medium'>Họ tên: </Typography>
          <Typography className='text-base'>{detail.fullName}</Typography>
        </div>
        <div className='flex gap-2'>
          <Typography className='w-40 text-lg font-medium'>Loại hợp đồng: </Typography>
          <Typography className='text-base'>{detail.contract?.contractType}</Typography>
        </div>

        <div className='flex gap-2'>
          <Typography className='w-40 text-lg font-medium'>Linked User: </Typography>
          <Space direction='vertical' style={{ width: '100%' }}>
            {detail.baseId ? (
              <Typography className='text-base text-green-600'>
                Linked to User ID: {detail.baseId}
              </Typography>
            ) : (
              <Typography className='text-base text-gray-400'>Not linked</Typography>
            )}
            <Button type='default' onClick={() => setShowLinkUserModal(true)} size='small'>
              {detail.baseId ? 'Update Link' : 'Link to User'}
            </Button>
          </Space>
        </div>

        <div className='flex gap-2'>
          <Typography className='w-40 text-lg font-medium'>Ca làm việc: </Typography>
          <CoreSelect
            className='text-base w-60'
            control={control}
            name='workShift.id'
            optionsProps={workShifts}
            valueField='id'
            displayField='name'
            placeholder={'Chọn ca làm việc'}
          />
        </div>
        <div className='flex mt-2'>
          <Button type='primary' htmlType='submit'>
            Save
          </Button>
        </div>

        <Modal
          title='Link Employee to User'
          open={showLinkUserModal}
          onCancel={() => {
            setShowLinkUserModal(false);
            setBaseIdInput('');
          }}
          onOk={handleLinkUser}
          confirmLoading={loadingBaseId}
          okButtonProps={{ disabled: !baseIdInput.trim() }}
        >
          <Space direction='vertical' style={{ width: '100%' }}>
            <Typography.Text>Enter the User ID (Base ID) to link to this employee:</Typography.Text>
            <Input
              value={baseIdInput}
              onChange={(e) => setBaseIdInput(e.target.value)}
              placeholder='Enter User ID'
              allowClear
            />
            {detail.baseId && (
              <Typography.Text type='secondary'>
                Current linked User ID: {detail.baseId}
              </Typography.Text>
            )}
          </Space>
        </Modal>
      </form>
    </FormProvider>
  );
};
